/**
 * Client-side image optimization for uploads.
 *
 * Goal: drastically reduce upload weight and feed download weight without
 * altering app functionality. If compression fails for any reason, we fall
 * back to the original file so existing flows are never broken.
 *
 * - Only images are processed. Videos, PDFs and other binaries pass through.
 * - Animated GIFs pass through (browser-image-compression cannot preserve animation).
 * - Small images (< MIN_BYTES) pass through.
 * - Output format: WebP when supported by the browser, otherwise JPEG.
 */

import imageCompression from 'browser-image-compression';

const MIN_BYTES = 400 * 1024; // 400 KB — below this, skip compression
const DEFAULT_MAX_WIDTH = 1920;
const DEFAULT_MAX_SIZE_MB = 1; // target ~1 MB
const AVATAR_MAX_WIDTH = 800;
const AVATAR_MAX_SIZE_MB = 0.3; // target ~300 KB for avatars / thumbnails

let webpSupportCache: boolean | null = null;
function supportsWebP(): boolean {
  if (webpSupportCache !== null) return webpSupportCache;
  try {
    const canvas = document.createElement('canvas');
    canvas.width = 1;
    canvas.height = 1;
    webpSupportCache = canvas.toDataURL('image/webp').startsWith('data:image/webp');
  } catch {
    webpSupportCache = false;
  }
  return webpSupportCache;
}

export type OptimizePreset = 'feed' | 'avatar' | 'thumbnail';

export interface OptimizeOptions {
  preset?: OptimizePreset;
  maxWidthOrHeight?: number;
  maxSizeMB?: number;
}

function isCompressibleImage(file: File): boolean {
  if (!file.type.startsWith('image/')) return false;
  // Skip formats we don't want to recompress / can't preserve correctly
  if (file.type === 'image/gif') return false;
  if (file.type === 'image/svg+xml') return false;
  return true;
}

/**
 * Compresses an image File on the client. Returns the original file unchanged
 * if it's not an image, is already small, or if compression fails.
 */
export async function optimizeImage(
  file: File,
  options: OptimizeOptions = {}
): Promise<File> {
  try {
    if (!isCompressibleImage(file)) return file;
    if (file.size < MIN_BYTES && (options.preset ?? 'feed') === 'feed') {
      return file;
    }

    const preset = options.preset ?? 'feed';
    const maxWidthOrHeight =
      options.maxWidthOrHeight ??
      (preset === 'avatar' || preset === 'thumbnail'
        ? AVATAR_MAX_WIDTH
        : DEFAULT_MAX_WIDTH);
    const maxSizeMB =
      options.maxSizeMB ??
      (preset === 'avatar' || preset === 'thumbnail'
        ? AVATAR_MAX_SIZE_MB
        : DEFAULT_MAX_SIZE_MB);

    const useWebP = supportsWebP();
    const targetType = useWebP ? 'image/webp' : 'image/jpeg';

    const compressed = await imageCompression(file, {
      maxSizeMB,
      maxWidthOrHeight,
      useWebWorker: true,
      fileType: targetType,
      initialQuality: 0.82,
    });

    // Safety: if compression somehow produced a larger file, keep original.
    if (compressed.size >= file.size) return file;

    // Rebuild File with a sensible name/extension so downstream code that
    // splits by extension still works.
    const ext = useWebP ? 'webp' : 'jpg';
    const baseName = file.name.replace(/\.[^.]+$/, '') || 'image';
    const newName = `${baseName}.${ext}`;

    return new File([compressed], newName, {
      type: targetType,
      lastModified: Date.now(),
    });
  } catch (err) {
    console.warn('[optimizeImage] compression failed, using original file:', err);
    return file;
  }
}
